<template>
  <HrPageLayout
    title="Payroll ledger vs employee register"
    :breadcrumb-items="breadcrumbItems"
  >
    <div class="pcr-content">
      <div class="filters-card hr-mb-3">
        <div class="filters-grid">
          <div class="filter-group span-full">
            <label class="filter-label">Payroll ledger (Excel or CSV)</label>
            <input
              ref="fileInput"
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              class="filter-input"
              @change="onFile"
            >
          </div>
          <div class="filter-group">
            <label class="filter-label">Register scope</label>
            <select v-model="registerScope" class="filter-select">
              <option value="active">Active employees only</option>
              <option value="all">All employees (any status)</option>
            </select>
          </div>
          <div class="filter-group align-end">
            <button
              type="button"
              class="hr-btn hr-btn-primary"
              :disabled="loading || !csvRows.length"
              @click="runAnalysis"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              Load register &amp; analyze
            </button>
            <button
              type="button"
              class="hr-btn hr-btn-outline-secondary"
              :disabled="!analysisDone"
              @click="clearAll"
            >
              Clear
            </button>
            <button
              type="button"
              class="hr-btn hr-btn-success"
              :disabled="!analysisDone || savingReport"
              @click="openSaveReport"
            >
              {{ savingReport ? "Saving…" : "Save report" }}
            </button>
          </div>
        </div>

        <div v-if="headers.length" class="hr-mt-3 pcr-mapping">
          <h6 class="hr-mb-2">Column mapping</h6>
          <div class="mapping-grid">
            <div class="mapping-row">
              <span>Staff / ID (match to Emp No or National ID)</span>
              <select v-model="mapId" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'id-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
            <div class="mapping-row">
              <span>Department (for wage bill)</span>
              <select v-model="mapDept" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'dept-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
            <div class="mapping-row">
              <span>Pay amount (gross &gt; net &gt; basic)</span>
              <select v-model="mapPay" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'pay-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
            <div class="mapping-row">
              <span>Name (optional)</span>
              <select v-model="mapName" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'nm-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
            <div class="mapping-row">
              <span>Spending entity (costings grouping; not vote head)</span>
              <select v-model="mapSpendingEntity" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'se-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div v-if="errorMsg" class="alert alert-warning">{{ errorMsg }}</div>

      <div v-if="saveReportMessage" class="alert" :class="saveReportError ? 'alert-danger' : 'alert-success'">{{ saveReportMessage }}</div>
      <div v-if="analysisDone && activeTab !== 'saved'" class="hr-mb-2">
        <button type="button" class="hr-btn hr-btn-outline-secondary hr-btn-sm" @click="activeTab = 'saved'">
          Back to saved reports
        </button>
      </div>

      <ul class="nav nav-tabs hr-mb-3">
        <li v-for="t in tabs" :key="t.id" class="nav-item">
          <button
            type="button"
            class="nav-link"
            :class="{ active: activeTab === t.id, disabled: t.requiresAnalysis && !analysisDone }"
            :disabled="t.requiresAnalysis && !analysisDone"
            @click="onTabClick(t.id)"
          >
            {{ t.label }}
          </button>
        </li>
      </ul>

      <div v-show="activeTab === 'saved'" class="table-card hr-mb-3">
        <p class="text-muted hr-small">
          Saved reconciliation snapshots (summary, spending entities, wage bill, matched / missing lists). Run <strong>Save report</strong> after analysis to store a copy.
        </p>
        <button
          type="button"
          class="hr-btn hr-btn-outline-secondary hr-btn-sm hr-mb-2"
          :disabled="savedReportsLoading"
          @click="loadSavedReports"
        >
          Refresh list
        </button>
        <div class="table-responsive">
          <table class="table table-bordered table-sm">
            <thead>
              <tr>
                <th>Saved at</th>
                <th>Title</th>
                <th>Register scope</th>
                <th>Saved by</th>
                <th style="width: 200px">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="savedReportsLoading">
                <td colspan="5" class="text-muted">Loading…</td>
              </tr>
              <tr v-else-if="!savedReports.length">
                <td colspan="5" class="text-muted">No saved reports yet.</td>
              </tr>
              <tr v-for="r in savedReports" :key="'sr-' + r.id">
                <td>{{ r.created_at }}</td>
                <td>{{ r.title }}</td>
                <td>{{ r.register_scope }}</td>
                <td>{{ r.created_by_username || "—" }}</td>
                <td>
                  <button type="button" class="hr-btn hr-btn-primary hr-btn-sm" @click="loadSavedReport(r.id)">Load</button>
                  <button type="button" class="hr-btn hr-btn-outline-danger hr-btn-sm hr-ms-1" @click="deleteSavedReport(r.id)">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <template v-if="analysisDone">

        <div v-show="activeTab === 'summary'" class="table-card">
          <div class="d-flex flex-wrap gap-2 hr-mb-3">
            <button type="button" class="hr-btn hr-btn-success hr-btn-sm" @click="exportSummaryCsv">Download summary CSV</button>
          </div>
          <div class="row g-3 hr-mb-3">
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value">{{ summary.csvRowCount }}</div>
                <div class="pcr-stat-label">Rows in CSV</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value">{{ summary.registerCount }}</div>
                <div class="pcr-stat-label">Employees in register (scope)</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-success">{{ summary.matchedCount }}</div>
                <div class="pcr-stat-label">Matched (CSV ↔ register)</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-warning">{{ summary.csvOnlyCount }}</div>
                <div class="pcr-stat-label">In CSV, not in register</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-danger">{{ summary.registerOnlyCount }}</div>
                <div class="pcr-stat-label">Missing from payroll upload</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value">{{ formatMoney(summary.totalCsvPay) }}</div>
                <div class="pcr-stat-label">Total pay (CSV column)</div>
              </div>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'spending'" class="table-card">
          <p class="text-muted hr-small"><strong>SPENDING ENTITY (COSTINGS) SUMMARY</strong></p>
          <p v-if="!spendingSummaryRows.length && analysisDone" class="text-warning hr-small">
            No spending-entity column found (or no gross/basic). Set <strong>Spending entity</strong> in column mapping, or add a column such as “Spending Entities” / “County Spending Entity” to the payroll file.
          </p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('spending')">Download costing summary CSV</button>
          <div class="table-responsive" style="max-height: 520px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>County spending entity</th>
                  <th>Staff earning</th>
                  <th>Basic pay</th>
                  <th>Rental house all.</th>
                  <th>Commuter all.</th>
                  <th>Other allowances</th>
                  <th>Gross salary</th>
                  <th>NSSF</th>
                  <th>Housing levy</th>
                  <th>Pension (employer)</th>
                  <th>NITA</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in spendingSummaryRows" :key="'sp-' + row.seq + '-' + row.key">
                  <td>{{ row.seq }}</td>
                  <td>{{ row.entity }}</td>
                  <td>{{ row.staffCount }}</td>
                  <td>{{ formatMoney(row.basic) }}</td>
                  <td>{{ formatMoney(row.rental) }}</td>
                  <td>{{ formatMoney(row.commuter) }}</td>
                  <td>{{ formatMoney(row.otherAllow) }}</td>
                  <td>{{ formatMoney(row.gross) }}</td>
                  <td>{{ formatMoney(row.nssf) }}</td>
                  <td>{{ formatMoney(row.housing) }}</td>
                  <td>{{ formatMoney(row.pensionEmployer) }}</td>
                  <td>{{ formatMoney(row.nita) }}</td>
                  <td>{{ formatMoney(row.total) }}</td>
                </tr>
                <tr v-if="spendingSummaryRows.length" class="table-secondary fw-bold">
                  <td colspan="2">County wage bill (total)</td>
                  <td>{{ spendingTotals.staffCount }}</td>
                  <td>{{ formatMoney(spendingTotals.basic) }}</td>
                  <td>{{ formatMoney(spendingTotals.rental) }}</td>
                  <td>{{ formatMoney(spendingTotals.commuter) }}</td>
                  <td>{{ formatMoney(spendingTotals.otherAllow) }}</td>
                  <td>{{ formatMoney(spendingTotals.gross) }}</td>
                  <td>{{ formatMoney(spendingTotals.nssf) }}</td>
                  <td>{{ formatMoney(spendingTotals.housing) }}</td>
                  <td>{{ formatMoney(spendingTotals.pensionEmployer) }}</td>
                  <td>{{ formatMoney(spendingTotals.nita) }}</td>
                  <td>{{ formatMoney(spendingTotals.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'wagebill'" class="table-card">
          <p class="text-muted hr-small">Totals by department / work station column from the ledger (or mapped department field).</p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('wagebill')">Download CSV</button>
          <div class="table-responsive">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Department (CSV)</th>
                  <th>Headcount</th>
                  <th>Total pay</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in wageBillRows" :key="row.dept">
                  <td>{{ row.dept }}</td>
                  <td>{{ row.count }}</td>
                  <td>{{ formatMoney(row.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'spendingDetail'" class="table-card">
          <p class="text-muted hr-small">Detailed report per selected County Spending Entity (sample format).</p>
          <div class="d-flex flex-wrap gap-2 hr-mb-2">
            <select v-model="selectedSpendingEntityDetail" class="filter-select" style="max-width: 360px;">
              <option value="">All spending entities</option>
              <option v-for="e in spendingEntityOptions" :key="'seo-' + e" :value="e">{{ e }}</option>
            </select>
          </div>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('spendingDetail')">Download CSV</button>
          <div class="table-responsive" style="max-height: 520px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>PAYROLL No.</th>
                  <th>ID. No.</th>
                  <th>NAME</th>
                  <th>COUNTY SPENDING ENTITY</th>
                  <th>Basic Pay</th>
                  <th>Rental House All.</th>
                  <th>Commuter All.</th>
                  <th>Other Allowances</th>
                  <th>GrossPay</th>
                  <th>Projected Annual Increment (4% of Basic Pay)</th>
                  <th>NSSF</th>
                  <th>H/Levy</th>
                  <th>Pension (Employer)</th>
                  <th>NITA</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in filteredSpendingDetailRows" :key="'sdr-' + i">
                  <td>{{ i + 1 }}</td>
                  <td>{{ r.payrollNo }}</td>
                  <td>{{ r.nationalId }}</td>
                  <td>{{ r.name }}</td>
                  <td>{{ r.entity }}</td>
                  <td>{{ formatMoney(r.basic) }}</td>
                  <td>{{ formatMoney(r.rental) }}</td>
                  <td>{{ formatMoney(r.commuter) }}</td>
                  <td>{{ formatMoney(r.otherAllow) }}</td>
                  <td>{{ formatMoney(r.gross) }}</td>
                  <td>{{ formatMoney(r.projectedIncrement) }}</td>
                  <td>{{ formatMoney(r.nssf) }}</td>
                  <td>{{ formatMoney(r.housing) }}</td>
                  <td>{{ formatMoney(r.pensionEmployer) }}</td>
                  <td>{{ formatMoney(r.nita) }}</td>
                </tr>
                <tr v-if="filteredSpendingDetailRows.length" class="table-secondary fw-bold">
                  <td colspan="5">Totals</td>
                  <td>{{ formatMoney(spendingDetailTotals.basic) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.rental) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.commuter) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.otherAllow) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.gross) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.projectedIncrement) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.nssf) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.housing) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.pensionEmployer) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.nita) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'registerOnly'" class="table-card">
          <p class="text-muted hr-small"><strong>Missing from payroll upload:</strong> employees in HRMIS (register) not found on the file (matched by Payroll / Emp No or National ID / IDNum).</p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('registerOnly')">Download CSV</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Emp No</th>
                  <th>National ID</th>
                  <th>Name</th>
                  <th>Department (register)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(e, i) in registerOnlyRows" :key="'ro-' + i">
                  <td>{{ e.empNo }}</td>
                  <td>{{ e.nationalId }}</td>
                  <td>{{ fullName(e) }}</td>
                  <td>{{ e.departmentName || e.departmentID }}</td>
                  <td>{{ e.status }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'csvOnly'" class="table-card">
          <p class="text-muted hr-small">CSV rows whose staff ID did not match any employee (Emp No or National ID).</p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('csvOnly')">Download CSV</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Staff ID (CSV)</th>
                  <th>Name</th>
                  <th>Department</th>
                  <th>Pay</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in csvOnlyRows" :key="'co-' + i">
                  <td>{{ r.staffId }}</td>
                  <td>{{ r.name }}</td>
                  <td>{{ r.dept }}</td>
                  <td>{{ formatMoney(r.pay) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'matched'" class="table-card">
          <p class="text-muted hr-small">Rows where CSV staff ID matched the register. “Dept mismatch” flags when CSV department text ≠ register department name.</p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('matched')">Download CSV</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Emp No</th>
                  <th>Name (register)</th>
                  <th>Dept (register)</th>
                  <th>Dept (CSV)</th>
                  <th>Dept mismatch</th>
                  <th>Pay (CSV)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in matchedDetailRows" :key="'m-' + i">
                  <td>{{ r.empNo }}</td>
                  <td>{{ r.regName }}</td>
                  <td>{{ r.regDept }}</td>
                  <td>{{ r.csvDept }}</td>
                  <td>{{ r.deptMismatch ? 'Yes' : '' }}</td>
                  <td>{{ formatMoney(r.pay) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";
import Papa from "papaparse";
import * as XLSX from "xlsx";

function normId(v) {
  if (v == null || v === "") return "";
  return String(v).trim().replace(/\s+/g, "").toUpperCase();
}

function normKeyHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function pickColumn(headers, patterns) {
  const list = headers.map((h) => ({ raw: h, key: normKeyHeader(h) }));
  for (const p of patterns) {
    const pl = p.toLowerCase();
    const exact = list.find((x) => x.key === pl);
    if (exact) return exact.raw;
  }
  for (const p of patterns) {
    const pl = p.toLowerCase();
    const sub = list.find((x) => x.key.includes(pl));
    if (sub) return sub.raw;
  }
  return null;
}

function pickSpendingEntityColumn(headers) {
  const direct =
    pickColumn(headers, [
      "spending entities",
      "spending entity",
      "county spending entity",
      "county spending entities",
      "spending entit",
      "county spending entit",
      "cost centre",
      "cost center",
      "costcentre"
    ]) || "";
  if (direct) return direct;
  const list = (headers || []).map((h) => String(h || "").trim());
  const normalize = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const strictPreferred = list.find((h) => {
    const n = normalize(h);
    return n === "spendingentities" || n === "countyspendingentity" || n === "countyspendingentities";
  });
  if (strictPreferred) return strictPreferred;
  const fuzzy = list.find((h) => {
    const n = normalize(h);
    return n.includes("spend") && (n.includes("entity") || n.includes("entit"));
  });
  return fuzzy || null;
}

function parseMoney(v) {
  if (v == null || v === "") return 0;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  const s = String(v).replace(/,/g, "").replace(/[^\d.-]/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

export default {
  name: "PayrollCsvReconciliation",
  components: { HrPageLayout },
  data() {
    return {
      breadcrumbItems: [
        { label: "Reports & Analytics", route: "/reports-hub" },
        { label: "Payroll ledger reconciliation" }
      ],
      csvRows: [],
      headers: [],
      registerScope: "active",
      loading: false,
      errorMsg: "",
      analysisDone: false,
      activeTab: "saved",
      mapId: "",
      mapDept: "",
      mapPay: "",
      mapName: "",
      mapSpendingEntity: "",
      selectedSpendingEntityDetail: "",
      employees: [],
      savedReports: [],
      savedReportsLoading: false,
      savingReport: false,
      saveReportMessage: "",
      saveReportError: false,
      summary: {
        csvRowCount: 0,
        registerCount: 0,
        matchedCount: 0,
        csvOnlyCount: 0,
        registerOnlyCount: 0,
        totalCsvPay: 0
      },
      wageBillRows: [],
      registerOnlyRows: [],
      csvOnlyRows: [],
      matchedDetailRows: [],
      spendingSummaryRows: [],
      spendingDetailRows: [],
      tabs: [
        { id: "saved", label: "Saved reports", requiresAnalysis: false },
        { id: "summary", label: "Summary", requiresAnalysis: true },
        { id: "spending", label: "Spending entities (costings)", requiresAnalysis: true },
        { id: "spendingDetail", label: "Spending entity detailed", requiresAnalysis: true },
        { id: "wagebill", label: "Dept / station wage bill", requiresAnalysis: true },
        { id: "registerOnly", label: "Missing from payroll upload", requiresAnalysis: true },
        { id: "csvOnly", label: "On payroll, not in register", requiresAnalysis: true },
        { id: "matched", label: "Matched detail", requiresAnalysis: true }
      ]
    };
  },
  computed: {
    spendingTotals() {
      const z = {
        staffCount: 0,
        basic: 0,
        rental: 0,
        commuter: 0,
        otherAllow: 0,
        gross: 0,
        nssf: 0,
        housing: 0,
        pensionEmployer: 0,
        nita: 0,
        total: 0
      };
      for (const row of this.spendingSummaryRows) {
        z.staffCount += Number(row.staffCount) || 0;
        z.basic += Number(row.basic) || 0;
        z.rental += Number(row.rental) || 0;
        z.commuter += Number(row.commuter) || 0;
        z.otherAllow += Number(row.otherAllow) || 0;
        z.gross += Number(row.gross) || 0;
        z.nssf += Number(row.nssf) || 0;
        z.housing += Number(row.housing) || 0;
        z.pensionEmployer += Number(row.pensionEmployer) || 0;
        z.nita += Number(row.nita) || 0;
        z.total += Number(row.total) || 0;
      }
      return z;
    },
    spendingEntityOptions() {
      const set = new Set();
      (this.spendingDetailRows || []).forEach((r) => {
        const k = String(r.entity || "").trim();
        if (k) set.add(k);
      });
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    },
    filteredSpendingDetailRows() {
      const selected = String(this.selectedSpendingEntityDetail || "").trim();
      if (!selected) return this.spendingDetailRows || [];
      return (this.spendingDetailRows || []).filter((r) => String(r.entity || "").trim() === selected);
    },
    spendingDetailTotals() {
      const z = {
        basic: 0,
        rental: 0,
        commuter: 0,
        otherAllow: 0,
        gross: 0,
        projectedIncrement: 0,
        nssf: 0,
        housing: 0,
        pensionEmployer: 0,
        nita: 0
      };
      for (const r of this.filteredSpendingDetailRows || []) {
        z.basic += Number(r.basic) || 0;
        z.rental += Number(r.rental) || 0;
        z.commuter += Number(r.commuter) || 0;
        z.otherAllow += Number(r.otherAllow) || 0;
        z.gross += Number(r.gross) || 0;
        z.projectedIncrement += Number(r.projectedIncrement) || 0;
        z.nssf += Number(r.nssf) || 0;
        z.housing += Number(r.housing) || 0;
        z.pensionEmployer += Number(r.pensionEmployer) || 0;
        z.nita += Number(r.nita) || 0;
      }
      return z;
    }
  },
  mounted() {
    this.loadSavedReports();
  },
  methods: {
    fullName(e) {
      const m = e.middleName ? ` ${e.middleName} ` : " ";
      return `${e.firstName || ""}${m}${e.lastName || ""}`.trim();
    },
    formatMoney(n) {
      if (n == null || Number.isNaN(n)) return "—";
      return new Intl.NumberFormat("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    },
    onTabClick(id) {
      const t = this.tabs.find((x) => x.id === id);
      if (t && t.requiresAnalysis && !this.analysisDone) return;
      this.activeTab = id;
      if (id === "saved") this.loadSavedReports();
    },
    async loadSavedReports() {
      this.savedReportsLoading = true;
      try {
        const data = new FormData();
        data.append("function", "listPayrollCsvReconciliationReports");
        data.append("limit", "50");
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          this.savedReports = res.data.data?.reports || [];
        } else {
          this.savedReports = [];
        }
      } catch (_) {
        this.savedReports = [];
      } finally {
        this.savedReportsLoading = false;
      }
    },
    openSaveReport() {
      this.saveReportMessage = "";
      const def = `Payroll reconciliation ${new Date().toISOString().slice(0, 10)}`;
      const title = window.prompt("Title for this saved report", def);
      if (title === null) return;
      this.saveReportToServer(title.trim() || def);
    },
    buildReportPayload() {
      return {
        version: 1,
        summary: { ...this.summary },
        spendingSummaryRows: this.spendingSummaryRows,
        wageBillRows: this.wageBillRows,
        registerOnlyRows: this.registerOnlyRows,
        csvOnlyRows: this.csvOnlyRows,
        matchedDetailRows: this.matchedDetailRows,
        spendingDetailRows: this.spendingDetailRows,
        columnMaps: {
          mapId: this.mapId,
          mapDept: this.mapDept,
          mapPay: this.mapPay,
          mapName: this.mapName,
          mapSpendingEntity: this.mapSpendingEntity
        },
        headers: [...this.headers]
      };
    },
    async saveReportToServer(title) {
      this.savingReport = true;
      this.saveReportMessage = "";
      this.saveReportError = false;
      try {
        const payload = JSON.stringify(this.buildReportPayload());
        const data = new FormData();
        data.append("function", "savePayrollCsvReconciliationReport");
        data.append("title", title);
        data.append("registerScope", this.registerScope);
        data.append("payload", payload);
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          this.saveReportMessage = res.data.message || "Report saved.";
          this.saveReportError = false;
          await this.loadSavedReports();
          this.activeTab = "saved";
        } else {
          this.saveReportMessage = res.data?.message || "Could not save report.";
          this.saveReportError = true;
        }
      } catch (e) {
        this.saveReportMessage = e.message || "Could not save report.";
        this.saveReportError = true;
      } finally {
        this.savingReport = false;
      }
    },
    async loadSavedReport(id) {
      this.errorMsg = "";
      try {
        const data = new FormData();
        data.append("function", "getPayrollCsvReconciliationReport");
        data.append("id", String(id));
        const res = await execute(data, authUrl);
        if (!res.data || !res.data.success) {
          throw new Error(res.data?.message || "Failed to load report");
        }
        const rep = res.data.data?.report;
        const p = rep?.payload;
        if (!p || typeof p !== "object") {
          throw new Error("Invalid saved data");
        }
        this.applyReportPayload(p);
        if (rep.register_scope) this.registerScope = rep.register_scope;
        this.analysisDone = true;
        this.activeTab = "summary";
        this.saveReportMessage = `Loaded: ${rep.title || "Report #" + id}`;
        this.saveReportError = false;
      } catch (e) {
        this.errorMsg = e.message || String(e);
      }
    },
    applyReportPayload(p) {
      this.summary = {
        csvRowCount: p.summary?.csvRowCount ?? 0,
        registerCount: p.summary?.registerCount ?? 0,
        matchedCount: p.summary?.matchedCount ?? 0,
        csvOnlyCount: p.summary?.csvOnlyCount ?? 0,
        registerOnlyCount: p.summary?.registerOnlyCount ?? 0,
        totalCsvPay: p.summary?.totalCsvPay ?? 0
      };
      const srows = Array.isArray(p.spendingSummaryRows) ? p.spendingSummaryRows : [];
      this.spendingSummaryRows = srows.map((r, i) => ({
        ...r,
        key: r.key != null ? r.key : `legacy-${r.seq ?? i}-${r.entity ?? ""}`
      }));
      this.wageBillRows = Array.isArray(p.wageBillRows) ? p.wageBillRows : [];
      this.registerOnlyRows = Array.isArray(p.registerOnlyRows) ? p.registerOnlyRows : [];
      this.csvOnlyRows = Array.isArray(p.csvOnlyRows) ? p.csvOnlyRows : [];
      this.matchedDetailRows = Array.isArray(p.matchedDetailRows) ? p.matchedDetailRows : [];
      this.spendingDetailRows = Array.isArray(p.spendingDetailRows) ? p.spendingDetailRows : [];
      const cm = p.columnMaps || {};
      this.mapId = cm.mapId ?? "";
      this.mapDept = cm.mapDept ?? "";
      this.mapPay = cm.mapPay ?? "";
      this.mapName = cm.mapName ?? "";
      this.mapSpendingEntity = cm.mapSpendingEntity ?? "";
      this.headers = Array.isArray(p.headers) ? [...p.headers] : [];
      this.csvRows = [];
    },
    async deleteSavedReport(id) {
      if (!window.confirm("Delete this saved report?")) return;
      try {
        const data = new FormData();
        data.append("function", "deletePayrollCsvReconciliationReport");
        data.append("id", String(id));
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          await this.loadSavedReports();
        } else {
          this.errorMsg = res.data?.message || "Delete failed";
        }
      } catch (e) {
        this.errorMsg = e.message || String(e);
      }
    },
    onFile(ev) {
      const f = ev.target.files && ev.target.files[0];
      this.errorMsg = "";
      this.analysisDone = false;
      this.csvRows = [];
      this.headers = [];
      if (!f) return;
      const name = (f.name || "").toLowerCase();
      if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const wb = XLSX.read(data, { type: "array" });
            const sheetName = wb.SheetNames[0];
            const sheet = wb.Sheets[sheetName];
            const json = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
            const rows = json
              .map((row) => {
                const o = {};
                for (const k of Object.keys(row)) {
                  o[String(k).trim()] = row[k];
                }
                return o;
              })
              .filter((r) => Object.keys(r).some((k) => String(r[k] || "").trim() !== ""));
            this.csvRows = rows;
            this.headers = rows[0] ? Object.keys(rows[0]) : [];
            this.autoMapColumns();
          } catch (err) {
            this.errorMsg = err.message || "Failed to read Excel file";
          }
        };
        reader.onerror = () => {
          this.errorMsg = "Could not read the file";
        };
        reader.readAsArrayBuffer(f);
        return;
      }
      Papa.parse(f, {
        header: true,
        skipEmptyLines: "greedy",
        complete: (res) => {
          const rows = (res.data || []).filter((r) => Object.keys(r).some((k) => String(r[k] || "").trim() !== ""));
          this.csvRows = rows;
          this.headers = res.meta.fields || (rows[0] ? Object.keys(rows[0]) : []);
          this.autoMapColumns();
        },
        error: (err) => {
          this.errorMsg = err.message || "Failed to read CSV";
        }
      });
    },
    autoMapColumns() {
      const h = this.headers;
      this.mapId =
        pickColumn(h, [
          "payrollnum",
          "payroll num",
          "idnum",
          "id num",
          "national id",
          "nationalid",
          "pf no",
          "p/f no",
          "pfno",
          "emp no",
          "employee no",
          "staff no",
          "personal file",
          "service number",
          "id no",
          "staff id",
          "employee id",
          "empno",
          "pf number",
          "taxpin",
          "tax pin"
        ]) || "";
      this.mapDept =
        pickColumn(h, [
          "wstationname",
          "work station",
          "station",
          "department",
          "dept",
          "directorate",
          "unit",
          "wcountyname",
          "county"
        ]) || "";
      this.mapPay =
        pickColumn(h, [
          "grosspay",
          "gross pay",
          "gross salary",
          "gross",
          "net pay",
          "net salary",
          "netpay",
          "net",
          "basic pay",
          "basic salary",
          "basic",
          "total pay",
          "amount",
          "pay"
        ]) || "";
      this.mapName =
        pickColumn(h, ["names", "employee name", "staff name", "full name", "name", "surname"]) || "";
      this.mapSpendingEntity =
        pickSpendingEntityColumn(h) || "";
    },
    resolvedMaps() {
      const h = this.headers;
      const idCol =
        this.mapId ||
        pickColumn(h, ["payrollnum", "idnum", "pf", "emp", "staff", "employee", "national"]);
      const deptCol =
        this.mapDept ||
        pickColumn(h, ["department", "dept", "wstation", "station", "work station", "wstationname"]);
      let payCol = this.mapPay;
      if (!payCol) {
        payCol =
          pickColumn(h, ["grosspay", "gross"]) ||
          pickColumn(h, ["net"]) ||
          pickColumn(h, ["basic"]) ||
          pickColumn(h, ["amount", "pay", "salary"]);
      }
      const nameCol = this.mapName || pickColumn(h, ["names", "name"]);
      return { idCol, deptCol, payCol, nameCol };
    },

    /** Costings summary: one row per county spending entity (ledger column), not vote head. */
    computeSpendingSummary() {
      const h = this.headers;
      const entityCol =
        this.mapSpendingEntity ||
        pickColumn(h, [
          "spending entities",
          "spending entity",
          "county spending entity",
          "cost centre",
          "cost center",
          "costcentre"
        ]);
      const basicCol = pickColumn(h, ["basic salary", "basic pay"]);
      const rentalCol = pickColumn(h, ["rental house all"]);
      const commuterCol = pickColumn(h, ["commuter all"]);
      const grossCol = pickColumn(h, ["grosspay", "gross pay", "gross salary"]);
      const nssfCol =
        pickColumn(h, ["nssf auto"]) ||
        pickColumn(h, ["nssf (arrears", "nssf arrears"]) ||
        pickColumn(h, ["nssf"]);
      const housingCol = pickColumn(h, ["housing levy fund", "housing levy"]);
      const pensionEmpCol = pickColumn(h, ["pension (employer)", "pension employer", "employer pension"]);
      const nitaCol = pickColumn(h, ["nita"]);

      if (!entityCol) return [];
      if (!grossCol && !basicCol) return [];

      const m = new Map();
      for (const row of this.csvRows) {
        let raw = row[entityCol] != null ? String(row[entityCol]).trim() : "";
        const normKey = raw === "" ? "__none__" : raw.toLowerCase();
        if (raw === "") raw = "(Unspecified spending entity)";
        const agg = m.get(normKey) || {
          entityLabel: raw,
          staffCount: 0,
          basic: 0,
          rental: 0,
          commuter: 0,
          gross: 0,
          nssf: 0,
          housing: 0,
          pensionEmployer: 0,
          nita: 0
        };
        agg.staffCount += 1;
        if (basicCol) agg.basic += parseMoney(row[basicCol]);
        if (rentalCol) agg.rental += parseMoney(row[rentalCol]);
        if (commuterCol) agg.commuter += parseMoney(row[commuterCol]);
        if (grossCol) agg.gross += parseMoney(row[grossCol]);
        else agg.gross += parseMoney(row[basicCol]) + parseMoney(row[rentalCol]) + parseMoney(row[commuterCol]);
        if (nssfCol) agg.nssf += parseMoney(row[nssfCol]);
        if (housingCol) agg.housing += parseMoney(row[housingCol]);
        if (pensionEmpCol) agg.pensionEmployer += parseMoney(row[pensionEmpCol]);
        if (nitaCol) agg.nita += parseMoney(row[nitaCol]);
        m.set(normKey, agg);
      }

      const out = [];
      for (const [normKey, agg] of m.entries()) {
        const otherAllow = Math.max(0, agg.gross - agg.basic - agg.rental - agg.commuter);
        const total = agg.gross + agg.nssf + agg.housing + agg.pensionEmployer + agg.nita;
        const label = agg.entityLabel || "(Unspecified spending entity)";
        out.push({
          key: normKey,
          seq: 0,
          entity: label,
          staffCount: agg.staffCount,
          basic: agg.basic,
          rental: agg.rental,
          commuter: agg.commuter,
          otherAllow,
          gross: agg.gross,
          nssf: agg.nssf,
          housing: agg.housing,
          pensionEmployer: agg.pensionEmployer,
          nita: agg.nita,
          total
        });
      }
      out.sort((a, b) => String(a.entity).localeCompare(String(b.entity)));
      out.forEach((r, i) => {
        r.seq = i + 1;
      });
      return out;
    },
    computeSpendingDetailRows() {
      const h = this.headers;
      const entityCol = this.mapSpendingEntity || pickSpendingEntityColumn(h);
      const idCol = this.mapId || pickColumn(h, ["payrollnum", "idnum", "pf", "emp", "staff", "employee", "national"]);
      const nameCol = this.mapName || pickColumn(h, ["names", "employee name", "staff name", "full name", "name"]);
      const payrollNoCol = pickColumn(h, ["payrollnum", "payroll num", "pf no", "p/f no", "pf number", "personal number", "emp no", "employee no"]);
      const nationalIdCol = pickColumn(h, ["national id", "id no", "idnum", "nationalid"]);
      const basicCol = pickColumn(h, ["basic salary", "basic pay", "basic"]);
      const rentalCol = pickColumn(h, ["rental house all", "house allowance", "rental"]);
      const commuterCol = pickColumn(h, ["commuter all", "commuter allowance", "commuter"]);
      const grossCol = pickColumn(h, ["grosspay", "gross pay", "gross salary", "gross"]);
      const nssfCol = pickColumn(h, ["nssf auto", "nssf"]);
      const housingCol = pickColumn(h, ["housing levy fund", "housing levy"]);
      const pensionEmployerCol = pickColumn(h, ["pension (employer)", "pension employer", "employer pension"]);
      const nitaCol = pickColumn(h, ["nita"]);
      if (!entityCol) return [];
      const rows = this.csvRows.map((row) => ({
        entity: String(row[entityCol] || "(Unspecified spending entity)").trim() || "(Unspecified spending entity)",
        staffId: idCol ? (row[idCol] || "") : "",
        payrollNo: payrollNoCol ? (row[payrollNoCol] || "") : "",
        nationalId: nationalIdCol ? (row[nationalIdCol] || "") : "",
        name: nameCol ? (row[nameCol] || "") : "",
        basic: basicCol ? parseMoney(row[basicCol]) : 0,
        rental: rentalCol ? parseMoney(row[rentalCol]) : 0,
        commuter: commuterCol ? parseMoney(row[commuterCol]) : 0,
        gross: grossCol ? parseMoney(row[grossCol]) : 0,
        nssf: nssfCol ? parseMoney(row[nssfCol]) : 0,
        housing: housingCol ? parseMoney(row[housingCol]) : 0,
        pensionEmployer: pensionEmployerCol ? parseMoney(row[pensionEmployerCol]) : 0,
        nita: nitaCol ? parseMoney(row[nitaCol]) : 0
      }));
      rows.forEach((r) => {
        r.otherAllow = Math.max(0, (Number(r.gross) || 0) - (Number(r.basic) || 0) - (Number(r.rental) || 0) - (Number(r.commuter) || 0));
        r.projectedIncrement = (Number(r.basic) || 0) * 0.04;
        r.total = (Number(r.gross) || 0) + (Number(r.nssf) || 0) + (Number(r.housing) || 0) + (Number(r.pensionEmployer) || 0) + (Number(r.nita) || 0);
      });
      rows.sort((a, b) => String(a.entity).localeCompare(String(b.entity)));
      return rows;
    },
    downloadSampleCsv() {
      const lines = [
        "P/F No,Employee Name,Department,Basic Pay,Gross Pay,Net Pay",
        "PF001,Jane Doe,Finance,50000,75000,65000",
        "PF002,John Smith,Health,80000,120000,95000"
      ];
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "sample_payroll_reconciliation.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    },
    async fetchAllEmployees() {
      const all = [];
      let page = 1;
      const rows_per_page = 5000;
      let totalItems = null;
      /* eslint-disable no-await-in-loop */
      while (true) {
        const data = new FormData();
        data.append("function", "getEmployees");
        data.append("page", String(page));
        data.append("rows_per_page", String(rows_per_page));
        const res = await execute(data, authUrl);
        if (!res.data || !res.data.success) {
          throw new Error(res.data?.message || "Failed to load employees");
        }
        const payload = res.data.data || {};
        const batch = Array.isArray(payload.employees) ? payload.employees : [];
        all.push(...batch);
        totalItems = payload.totalItems != null ? Number(payload.totalItems) : all.length;
        if (batch.length === 0 || all.length >= totalItems) break;
        page += 1;
        if (page > 500) break;
      }
      return all;
    },
    filterRegister(list) {
      if (this.registerScope === "all") return list;
      return list.filter((e) => {
        const s = (e.status || "").toString().toLowerCase();
        return s === "active" || s === "";
      });
    },
    buildRegisterIndex(registerList) {
      const byId = new Map();
      for (const e of registerList) {
        const a = normId(e.empNo);
        const b = normId(e.nationalId);
        if (a) byId.set(a, e);
        if (b) byId.set(b, e);
      }
      return byId;
    },
    deptMismatch(regDept, csvDept) {
      const a = (regDept || "").toString().trim().toLowerCase();
      const b = (csvDept || "").toString().trim().toLowerCase();
      if (!b) return false;
      return a !== b;
    },
    async runAnalysis() {
      this.errorMsg = "";
      if (!this.csvRows.length) {
        this.errorMsg = "Choose a payroll file (Excel or CSV) first.";
        return;
      }
      const { idCol, deptCol, payCol, nameCol } = this.resolvedMaps();
      if (!idCol) {
        this.errorMsg = "Could not detect a staff / employee ID column. Set “Staff / ID” in column mapping.";
        return;
      }
      if (!payCol) {
        this.errorMsg = "Could not detect a pay column. Set “Pay amount” in column mapping.";
        return;
      }

      this.loading = true;
      try {
        const raw = await this.fetchAllEmployees();
        const register = this.filterRegister(raw);
        const byId = this.buildRegisterIndex(register);

        const matchedIds = new Set();
        let totalCsvPay = 0;
        const wageMap = new Map();
        const csvOnly = [];
        const matchedDetail = [];

        for (const row of this.csvRows) {
          const rawId = row[idCol];
          const nid = normId(rawId);
          const pay = parseMoney(row[payCol]);
          totalCsvPay += pay;
          const d = (deptCol && row[deptCol] != null ? String(row[deptCol]).trim() : "") || "(No department)";
          const prev = wageMap.get(d) || { total: 0, count: 0 };
          prev.total += pay;
          prev.count += 1;
          wageMap.set(d, prev);

          const emp = nid ? byId.get(nid) : null;
          const nm = nameCol ? String(row[nameCol] || "").trim() : "";

          if (emp) {
            const e1 = normId(emp.empNo);
            const e2 = normId(emp.nationalId);
            if (e1) matchedIds.add(e1);
            if (e2) matchedIds.add(e2);
            const regDept = emp.departmentName || "";
            matchedDetail.push({
              empNo: emp.empNo,
              regName: this.fullName(emp),
              regDept,
              csvDept: d,
              deptMismatch: this.deptMismatch(regDept, d),
              pay
            });
          } else {
            csvOnly.push({ staffId: rawId, name: nm, dept: d, pay });
          }
        }

        const actuallyRegisterOnly = register.filter((e) => {
          const k1 = normId(e.empNo);
          const k2 = normId(e.nationalId);
          const matched =
            (k1 && matchedIds.has(k1)) ||
            (k2 && matchedIds.has(k2));
          return !matched;
        });

        this.wageBillRows = Array.from(wageMap.entries())
          .map(([dept, v]) => ({ dept, total: v.total, count: v.count }))
          .sort((a, b) => b.total - a.total);

        this.registerOnlyRows = actuallyRegisterOnly;
        this.csvOnlyRows = csvOnly;
        this.matchedDetailRows = matchedDetail;
        this.spendingSummaryRows = this.computeSpendingSummary();
        this.spendingDetailRows = this.computeSpendingDetailRows();

        this.summary = {
          csvRowCount: this.csvRows.length,
          registerCount: register.length,
          matchedCount: matchedDetail.length,
          csvOnlyCount: csvOnly.length,
          registerOnlyCount: actuallyRegisterOnly.length,
          totalCsvPay
        };

        this.analysisDone = true;
        this.activeTab = "summary";
      } catch (e) {
        this.errorMsg = e.message || String(e);
      } finally {
        this.loading = false;
      }
    },
    clearAll() {
      this.csvRows = [];
      this.headers = [];
      this.analysisDone = false;
      this.errorMsg = "";
      this.saveReportMessage = "";
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      this.mapId = this.mapDept = this.mapPay = this.mapName = this.mapSpendingEntity = "";
      this.spendingSummaryRows = [];
      this.spendingDetailRows = [];
      this.wageBillRows = [];
      this.registerOnlyRows = [];
      this.csvOnlyRows = [];
      this.matchedDetailRows = [];
      this.activeTab = "saved";
    },
    exportSummaryCsv() {
      const lines = [
        "Metric,Value",
        `CSV rows,${this.summary.csvRowCount}`,
        `Register employees (scope),${this.summary.registerCount}`,
        `Matched,${this.summary.matchedCount}`,
        `In CSV not in register,${this.summary.csvOnlyCount}`,
        `In register not in CSV,${this.summary.registerOnlyCount}`,
        `Total pay (CSV),${this.summary.totalCsvPay}`
      ];
      this.downloadBlob(lines.join("\n"), "payroll_reconciliation_summary.csv");
    },
    exportTableCsv(kind) {
      if (kind === "spending") {
        const hdr = [
          "S.No.",
          "County spending entity",
          "Staff earning",
          "Basic pay",
          "Rental house all.",
          "Commuter all.",
          "Other allowances",
          "Gross salary",
          "NSSF",
          "Housing levy",
          "Pension (employer)",
          "NITA",
          "Total"
        ];
        const body = this.spendingSummaryRows.map((r) =>
          [
            r.seq,
            this.csvEscape(r.entity),
            r.staffCount,
            r.basic,
            r.rental,
            r.commuter,
            r.otherAllow,
            r.gross,
            r.nssf,
            r.housing,
            r.pensionEmployer,
            r.nita,
            r.total
          ].join(",")
        );
        const t = this.spendingTotals;
        const totalLine = [
          "",
          this.csvEscape("County wage bill (total)"),
          t.staffCount,
          t.basic,
          t.rental,
          t.commuter,
          t.otherAllow,
          t.gross,
          t.nssf,
          t.housing,
          t.pensionEmployer,
          t.nita,
          t.total
        ].join(",");
        const lines = [hdr.join(",")].concat(body).concat(this.spendingSummaryRows.length ? [totalLine] : []);
        this.downloadBlob(lines.join("\n"), "SPENDING_ENTITY_COSTINGS_SUMMARY.csv");
      }
      if (kind === "wagebill") {
        const h = ["Department", "Headcount", "TotalPay"];
        const lines = [h.join(",")].concat(
          this.wageBillRows.map((r) =>
            [this.csvEscape(r.dept), r.count, r.total].join(",")
          )
        );
        this.downloadBlob(lines.join("\n"), "department_wage_bill_from_csv.csv");
      }
      if (kind === "spendingDetail") {
        const h = ["S. No.", "PAYROLL No.", "ID. No.", "NAME", "COUNTY SPENDING ENTITY", "Basic Pay", "Rental House All.", "Commuter All.", "Other Allowances", "GrossPay", "Projected Annual Increment (4% of Basic Pay)", "NSSF", "H/Levy", "Pension (Employer)", "NITA"];
        const list = this.filteredSpendingDetailRows;
        const lines = [h.join(",")].concat(
          list.map((r, i) =>
            [
              i + 1,
              this.csvEscape(r.payrollNo),
              this.csvEscape(r.nationalId),
              this.csvEscape(r.name),
              this.csvEscape(r.entity),
              r.basic, r.rental, r.commuter, r.otherAllow, r.gross, r.projectedIncrement, r.nssf, r.housing, r.pensionEmployer, r.nita
            ].join(",")
          )
        );
        this.downloadBlob(lines.join("\n"), "SPENDING_ENTITY_DETAILED_WAGEBILL.csv");
      }
      if (kind === "registerOnly") {
        const h = ["EmpNo", "NationalId", "Name", "Department", "Status"];
        const lines = [h.join(",")].concat(
          this.registerOnlyRows.map((e) =>
            [
              this.csvEscape(e.empNo),
              this.csvEscape(e.nationalId),
              this.csvEscape(this.fullName(e)),
              this.csvEscape(e.departmentName || e.departmentID),
              this.csvEscape(e.status)
            ].join(",")
          )
        );
        this.downloadBlob(lines.join("\n"), "register_not_in_csv.csv");
      }
      if (kind === "csvOnly") {
        const h = ["StaffId", "Name", "Department", "Pay"];
        const lines = [h.join(",")].concat(
          this.csvOnlyRows.map((r) =>
            [this.csvEscape(r.staffId), this.csvEscape(r.name), this.csvEscape(r.dept), r.pay].join(",")
          )
        );
        this.downloadBlob(lines.join("\n"), "csv_not_in_register.csv");
      }
      if (kind === "matched") {
        const h = ["EmpNo", "RegisterName", "RegisterDept", "CsvDept", "DeptMismatch", "Pay"];
        const lines = [h.join(",")].concat(
          this.matchedDetailRows.map((r) =>
            [
              this.csvEscape(r.empNo),
              this.csvEscape(r.regName),
              this.csvEscape(r.regDept),
              this.csvEscape(r.csvDept),
              r.deptMismatch ? "Yes" : "",
              r.pay
            ].join(",")
          )
        );
        this.downloadBlob(lines.join("\n"), "payroll_csv_matched.csv");
      }
    },
    csvEscape(v) {
      const s = v == null ? "" : String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    },
    downloadBlob(text, name) {
      const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
    }
  }
};
</script>

<style scoped>
.pcr-content {
  width: 100%;
  max-width: 100%;
}
.filters-card {
  background: var(--hr-card-bg, #fff);
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid var(--hr-border, #e5e7eb);
}
.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
  align-items: end;
}
.filter-group.span-full {
  grid-column: 1 / -1;
}
.filter-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}
.filter-input,
.filter-select {
  width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
}
.table-card {
  background: var(--hr-card-bg, #fff);
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid var(--hr-border, #e5e7eb);
}
.pcr-mapping {
  border-top: 1px solid #eee;
  padding-top: 1rem;
}
.mapping-grid {
  display: grid;
  gap: 0.75rem;
  max-width: 720px;
}
.mapping-row {
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 1rem;
  align-items: center;
  font-size: 0.875rem;
}
.pcr-stat {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
}
.pcr-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
}
.pcr-stat-label {
  font-size: 0.8rem;
  color: #6c757d;
}
.hr-mb-1 {
  margin-bottom: 0.25rem;
}
.hr-mb-2 {
  margin-bottom: 0.5rem;
}
.hr-mb-3 {
  margin-bottom: 1rem;
}
.hr-mt-1 {
  margin-top: 0.25rem;
}
.hr-mt-3 {
  margin-top: 1rem;
}
.hr-small {
  font-size: 0.875rem;
}
.hr-text-muted {
  color: #6c757d;
}
.hr-p-0 {
  padding: 0;
}
</style>
